import { useCallback, useEffect, useState } from 'react';
import { getSupabaseClient } from '../supabase-client';
import { createProject as createProjectWriter } from '../writers/create-project';
import { reportError } from '../../lib/monitoring';
import type { ProjectRow } from '../database.types';

/**
 * The Projects query.
 *
 * UI → THIS HOOK → writer wrappers → Postgres RPC (ARCHITECTURE §2). Screens
 * never touch the Supabase client, and reads go through `.select()` while every
 * write goes through a writer.
 *
 * Hand-rolled rather than TanStack Query: no caching library is locked, and
 * S-02 has exactly one list with one invalidation point. Adding a dependency
 * here would be a decision for a later slice that actually needs it.
 */

export type ProjectsStatus = 'loading' | 'ready' | 'failed';

export interface ProjectsState {
  status: ProjectsStatus;
  projects: ProjectRow[];
  /** Message shown when the list itself could not be loaded. */
  error: string | null;
  reload: () => Promise<void>;
  /**
   * Creates a Project and refreshes the list. Resolves to the new row, or
   * throws — the caller decides what to show, and nothing appears in the list
   * until the server has confirmed it.
   */
  create: (name: string) => Promise<ProjectRow>;
}

type FetchResult = { ok: true; rows: ProjectRow[] } | { ok: false };

export function useProjects(): ProjectsState {
  const client = getSupabaseClient();
  const [status, setStatus] = useState<ProjectsStatus>(client ? 'loading' : 'failed');
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [error, setError] = useState<string | null>(
    client ? null : 'Crewlink is not connected to a server yet.',
  );

  /** Pure fetch. Sets no state, so the effect below can decide when to apply it. */
  const fetchProjects = useCallback(async (): Promise<FetchResult> => {
    if (!client) return { ok: false };

    // Ordering is done in SQL so the list is stable regardless of what the
    // client does with it afterwards.
    const { data, error: queryError } = await client
      .from('project')
      .select('*')
      .order('created_at', { ascending: false });

    if (queryError) {
      reportError(queryError, 'projects.load');
      return { ok: false };
    }
    return { ok: true, rows: data };
  }, [client]);

  const applyResult = useCallback((result: FetchResult) => {
    if (!result.ok) {
      // Deliberately does NOT clear `projects`: if a refresh fails, showing the
      // list Brandon already had beats blanking the screen.
      setError('Could not load your Projects. Check your connection and try again.');
      setStatus('failed');
      return;
    }
    setProjects(result.rows);
    setError(null);
    setStatus('ready');
  }, []);

  useEffect(() => {
    // State is set from the promise callback rather than synchronously in the
    // effect body — the pattern the react-hooks rules ask for — and `cancelled`
    // stops a late response from updating an unmounted screen.
    let cancelled = false;
    void fetchProjects().then((result) => {
      if (!cancelled) applyResult(result);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchProjects, applyResult]);

  const reload = useCallback(async () => {
    applyResult(await fetchProjects());
  }, [fetchProjects, applyResult]);

  const create = useCallback<ProjectsState['create']>(
    async (name) => {
      if (!client) throw new Error('Crewlink is not connected to a server yet.');
      const row = await createProjectWriter(client, name);
      // Re-read rather than splicing the returned row in locally: the list then
      // reflects what the server actually holds, which is the same thing a
      // reload would show.
      applyResult(await fetchProjects());
      return row;
    },
    [client, fetchProjects, applyResult],
  );

  return { status, projects, error, reload, create };
}
